import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Reviews data hook for an entity.
 * - Fetches { summary, items, viewer_review } from /entities/{id}/reviews.
 * - Exposes create / update / delete / toggleHelpful with optimistic
 *   refetch so the summary header + breakdown bars stay in sync.
 * - Also returns the new entity aggregates implicitly via `onAggregateChange`,
 *   which lets parent pages (e.g. EntityDetail) update their header card
 *   without a full entity refetch.
 */
export function useReviews(entityId, { sort = 'recent', onAggregateChange } = {}) {
  const [summary, setSummary] = useState({ average: 0, total: 0, breakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
  const [items, setItems] = useState([]);
  const [viewerReview, setViewerReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/entities/${entityId}/reviews?sort=${sort}`,
        { withCredentials: true }
      );
      setSummary(res.data.summary || { average: 0, total: 0, breakdown: {} });
      setItems(res.data.items || []);
      setViewerReview(res.data.viewer_review || null);
      onAggregateChange?.({
        rating: res.data.summary?.average ?? 0,
        review_count: res.data.summary?.total ?? 0,
      });
    } catch (err) {
      console.error('reviews fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [entityId, sort, onAggregateChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(
    async ({ rating, title, body }) => {
      try {
        await axios.post(
          `${API}/entities/${entityId}/reviews`,
          { rating, title, body },
          { withCredentials: true }
        );
        toast.success('Review posted');
        await refresh();
        return true;
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (err.response?.status === 409 && detail?.error === 'review_already_exists') {
          toast.error('You already reviewed this — edit your existing review instead.');
        } else if (err.response?.status === 401) {
          toast.error('Log in to write a review');
        } else {
          toast.error(`Could not post review: ${detail || err.message}`);
        }
        return false;
      }
    },
    [entityId, refresh]
  );

  const update = useCallback(
    async (reviewId, patch) => {
      try {
        await axios.put(`${API}/reviews/${reviewId}`, patch, { withCredentials: true });
        toast.success('Review updated');
        await refresh();
        return true;
      } catch (err) {
        toast.error(`Could not update review: ${err.response?.data?.detail || err.message}`);
        return false;
      }
    },
    [refresh]
  );

  const remove = useCallback(
    async (reviewId) => {
      setBusyId(reviewId);
      try {
        const res = await axios.delete(`${API}/reviews/${reviewId}`, { withCredentials: true });
        toast.success(res.data?.deleted_by_admin ? 'Review removed by admin' : 'Review deleted');
        await refresh();
      } catch (err) {
        toast.error(`Could not delete review: ${err.response?.data?.detail || err.message}`);
      } finally {
        setBusyId(null);
      }
    },
    [refresh]
  );

  const toggleHelpful = useCallback(
    async (reviewId) => {
      setBusyId(reviewId);
      try {
        const res = await axios.post(
          `${API}/reviews/${reviewId}/helpful`,
          {},
          { withCredentials: true }
        );
        // Optimistic local patch — avoid a full refetch for a fast UX.
        const updateRow = (r) =>
          r.review_id === reviewId
            ? { ...r, helpful_count: res.data.helpful_count, viewer_marked_helpful: res.data.viewer_marked_helpful }
            : r;
        setItems((prev) => prev.map(updateRow));
        if (viewerReview?.review_id === reviewId) setViewerReview(updateRow(viewerReview));
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error('Log in to mark reviews as helpful');
        } else {
          toast.error(`Could not update: ${err.response?.data?.detail || err.message}`);
        }
        // Refetch on error so the UI doesn't get out of sync with the
        // backend's authoritative helpful_count / viewer flag.
        await refresh();
      } finally {
        setBusyId(null);
      }
    },
    [viewerReview, refresh]
  );

  return {
    summary,
    items,
    viewerReview,
    loading,
    busyId,
    refresh,
    create,
    update,
    remove,
    toggleHelpful,
  };
}
